import { useEffect, useMemo, useState } from "react";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import TableSearch from "../../../../../CommonElements/HeaderTableSearch";
import {
  Action,
  Amount,
  Category,
  Details,
  infoColor,
  NA,
  SearchTableButton,
  secondary,
  View,
} from "../../../../../utils/Constant";
import { IPettyCashExpenseTransactionsServiceResponse } from "../../../../../Types/PettyCash";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { P } from "../../../../../AbstractElements";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { dateConversion } from "../../../../../utils/dateUtils";
import { TableColumn } from "react-data-table-component";
import { useStaggeredRowStyles } from "../../../../../hooks/useStaggeredRowStyle";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";

export interface IManagerZoneTableProps {
  location: any;
  filterText: string;
  filteredData: Array<IPettyCashExpenseTransactionsServiceResponse>;
  setFilterText: (value: string) => void;
  openAttachmentModal: (
    data: IPettyCashExpenseTransactionsServiceResponse
  ) => void;
  openDialog: (id: number) => void;
  expenseListLoading: boolean;
}

const ManagerZoneTable = ({
  location,
  filterText,
  setFilterText,
  filteredData,
  openDialog,
  expenseListLoading,
  openAttachmentModal,
}: IManagerZoneTableProps) => {
  const [animate, setAnimate] = useState<boolean>(false);

  const conditionalRowStyles =
    useStaggeredRowStyles<IPettyCashExpenseTransactionsServiceResponse>(
      animate
    );

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <TableSearch
        value={filterText ? filterText : ""}
        label={SearchTableButton}
        containerClassName="dataTables_filter d-flex align-items-center mb-2"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFilterText(e.target.value)
        }
      />
    );
  }, [filterText]);

  const expenseListDetailsColumn = useMemo<
    TableColumn<IPettyCashExpenseTransactionsServiceResponse>[]
  >(() => {
    const baseCols: TableColumn<IPettyCashExpenseTransactionsServiceResponse>[] =
      [
        {
          name: "#",
          // It's generally better practice to use 'cell' for custom rendering like index
          cell: (_, index) => <P className="m-0">{index + 1}</P>,
          sortable: true,
          width: "60px",
        },
        {
          name: "Date",
          cell: (row) => (
            <P className="m-0 f-12">{dateConversion(row.date ?? "", true)}</P>
          ),
          sortable: true,
          width: "150px",
        },

        {
          name: Amount,
          cell: (row) => (
            <div className="d-flex align-items-center">
              <P className="m-0 f-12">{row.balance}</P>
              <BadgeDetails
                className="text-capitalize ms-2 rounded-pill"
                color={
                  row?.payment_type?.toLowerCase() === "online"
                    ? infoColor
                    : secondary
                }
                text={row?.payment_type ?? NA}
              />
            </div>
          ),
          sortable: true,
          width: "130px",
        },
        {
          name: Category,
          // Make sure to use the specific type for row if 'any' isn't needed
          cell: (row: IPettyCashExpenseTransactionsServiceResponse) => (
            <P className="m-0 f-12">
              {!location?.state?.walletCenterId && row?.category_name}
            </P>
          ),
          sortable: true,
          width: "200px",
        },
        {
          name: "Paid By",
          cell: (row: IPettyCashExpenseTransactionsServiceResponse) => (
            <P className="m-0 f-12">
              {!location?.state?.walletCenterId && row.user_name}
            </P>
          ),
          width: "180px",
        },
        {
          name: Details,
          cell: (row: IPettyCashExpenseTransactionsServiceResponse) => (
            <div className="">
              <P
                className="f-12 m-0 text-primary pointer text-underline"
                onClick={() => openAttachmentModal(row)}
              >
                {View}
              </P>
              <P className="m-0 f-12">{row.details}</P>
            </div>
          ),
          sortable: true,
          width: "180px",
        },
        {
          name: Action,
          cell: (row) => (
            <div
              {...(row.id !== null &&
                row.id !== undefined && {
                  onClick: () => openDialog(row.id as number),
                })}
            >
              <IconComponent className="icofont icofont-ui-delete text-danger f-16 pointer" />
            </div>
          ),
          sortable: true,
          width: "90px",
        },
      ];
    return baseCols;
  }, [
    dateConversion,
    infoColor,
    secondary,
    NA,
    location,
    openAttachmentModal,
    View,
    openDialog,
    Amount,
    Category,
    Details,
    Action,
  ]);

  const tableData = useMemo<
    (IPettyCashExpenseTransactionsServiceResponse & { _rowClass: string })[]
  >(() => {
    return filteredData.map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [filteredData]);

  useEffect(() => {
    if (!expenseListLoading && filteredData && filteredData.length > 0) {
      const timeout = setTimeout(() => setAnimate(true), 50);
      return () => clearTimeout(timeout);
    }
    setAnimate(false);
  }, [expenseListLoading, filteredData]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar mt-1 mt-md-4 d-none d-lg-block">
      <CustomDataTable
        striped
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        highlightOnHover
        data={tableData}
        columns={expenseListDetailsColumn}
        loading={expenseListLoading}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 8}
            columns={expenseListDetailsColumn}
            rowHeight={40}
            gap={4}
          />
        }
        conditionalRowStyles={conditionalRowStyles}
      />
    </div>
  );
};

export default ManagerZoneTable;
